import { useCallback } from "react";

import { useOrderDetailV2 } from "../OrderDetailV2Context";
import type { ActionsConfigMap } from "../types";

/**
 * Hook that centralises action execution for OrderDetailV2 components.
 *
 * Components receive an `ActionsConfigMap` (actionId → config) from the
 * transformer and pass it here.  The hook reads shared dependencies
 * (`bookingId`, `openUrl`, `orderActionHandlers`, `showActionModal`) from
 * context so individual components never need to know about them.
 *
 * Supported action types:
 *   • OPEN_URL       – opens a URL (supports {{bookingId}} interpolation)
 *   • API_EXECUTED   – calls a registered handler, then shows success / error modal
 */
export function useOrderActions(actionsConfig: ActionsConfigMap = {}) {
  const { bookingId, orderActionHandlers, showActionModal, openUrl } =
    useOrderDetailV2();

  const executeAction = useCallback(
    (actionId: string) => {
      const config = actionsConfig[actionId];
      if (!config) return;

      const type = String(config.type || "").toUpperCase();

      if (type === "OPEN_URL") {
        const url = (config.url || "").replace(
          /\{\{bookingId\}\}/g,
          bookingId || ""
        );
        openUrl?.(url, config.openInNewTab !== false);
      } else if (type === "API_EXECUTED") {
        const handler = orderActionHandlers?.[actionId];
        if (!handler) return;
        handler({ bookingId: bookingId || "" })
          .then(() => {
            if (config.successModal) {
              showActionModal?.(config.successModal);
            }
          })
          .catch(() => {
            if (config.errorModal) {
              showActionModal?.(config.errorModal);
            }
          });
      }
    },
    [actionsConfig, bookingId, orderActionHandlers, showActionModal, openUrl]
  );

  return { executeAction };
}
